import type { BookingBalance, BookingOpenNote, IsoDate } from '@lemontaigu/pms-core';
import { paymentState, type PaymentState } from './advanceView.js';
import { displayRoom, nightsBetween } from './departureSheet.js';

// « Suivi des paiements » (Encaissement, operator 2026-10-24) — how a booking's
// money reads, one rule per phase:
//
//   · to come or in house: the total against what was paid — the rule of the
//     « Paiement » column (paymentState), so the two never disagree;
//   · over: VEGA'S view (operator 2026-10-24). Every invoice of a departed
//     booking reads settled in Vega; what is still owed is a note left open, a
//     note put on a debtor account and not yet recovered, or lines posted and
//     never invoiced. A note whose arrhes exceed the bill reads negative — money
//     owed BACK — and arrhes Vega still holds that no open note absorbed were
//     never applied. Those two are « à régulariser », never netted against what
//     is owed. Comparing the total with the payments instead flagged 674
//     departed bookings on 2026-10-24, nearly all false.
//
// A note « en attente » on a booking Vega DID invoice is a SIDE note (operator
// 2026-10-24, PMS-PAYMENT-FOLLOW-UP-BRIEF §7): a guest's extras left behind,
// or a stray note — 022177's ch. 110, 51,00, on a booking paid in full. It no
// longer makes the booking « Impayé »; it is listed under « Notes en attente »,
// one row per note, for the desk to collect, cancel or put on account in Vega.
// A note on a booking Vega never invoiced is the stay itself, and is owed.
//
// What was recovered on a note put on a debtor account counts as paid (§8.8l).
//
// A booking made here (no Vega number) has no Vega view: it reads like one to come.

export type BalancePhase = 'future' | 'current' | 'past';

/** `owed` and `review` are a stay over's; the rest are PaymentState's. */
export type BalanceState = PaymentState | 'owed' | 'review' | 'settled' | 'none';

export interface BalanceReading {
  phase: BalancePhase;
  state: BalanceState;
  /** What the guest still owes: the total less what was paid (to come, in
   *  house); Vega's open notes, debts and unbilled lines (over). ≥ 0. */
  dueCents: number;
  /** A stay over: notes reading negative — owed back to the guest. ≥ 0. */
  refundCents: number;
  /** A stay over: arrhes Vega still holds that no open note absorbed. ≥ 0. */
  unappliedCents: number;
  /** Paid beyond the total (to come, in house). ≥ 0. */
  overpaidCents: number;
  /** A stay over that Vega invoiced: its notes still « en attente » — not
   *  owed by the booking, listed apart (« Notes en attente »). ≥ 0. */
  sideNoteCents: number;
}

export function balancePhase(b: Pick<BookingBalance, 'arrivalDate' | 'departureDate'>, today: IsoDate): BalancePhase {
  if (b.departureDate < today) return 'past';
  if (b.arrivalDate > today) return 'future';
  return 'current';
}

/** What was recovered on the booking's notes put on a debtor account (§8.8l)
 *  — a payment as much as a règlement, never more than the note moved. */
export function recoveredCents(b: Pick<BookingBalance, 'debts'>): number {
  return b.debts.reduce((t, d) => t + Math.min(Math.max(d.recoveredCents, 0), Math.max(d.cents, 0)), 0);
}

/** Everything paid towards the booking, a debtor recovery included. */
export function paidWithRecoveries(b: Pick<BookingBalance, 'paidCents' | 'debts'>): number {
  return b.paidCents + recoveredCents(b);
}

/** A stay over whose notes « en attente » are side notes: Vega invoiced it. */
const isInvoiced = (b: BookingBalance) => b.invoicedCents !== 0;

export function readBalance(b: BookingBalance, today: IsoDate): BalanceReading {
  const phase = balancePhase(b, today);
  const paid = paidWithRecoveries(b);
  if (phase !== 'past' || b.bookingRef === null) {
    return {
      phase,
      state: paymentState(paid, b.totalCents) ?? 'none',
      dueCents: Math.max(b.totalCents - paid, 0),
      refundCents: 0,
      unappliedCents: 0,
      overpaidCents: Math.max(paid - b.totalCents, 0),
      sideNoteCents: 0,
    };
  }
  const notesOwed = b.openNotes.reduce((t, n) => t + Math.max(n.cents, 0), 0);
  const refundCents = b.openNotes.reduce((t, n) => t + Math.max(-n.cents, 0), 0);
  const applied = b.openNotes.reduce((t, n) => t + n.appliedCents, 0);
  const sideNoteCents = isInvoiced(b) ? notesOwed : 0;
  const dueCents = notesOwed - sideNoteCents + Math.max(b.debtCents, 0) + Math.max(b.unbilledCents, 0);
  const unappliedCents = Math.max(b.depositCents - applied, 0);
  return {
    phase,
    state: dueCents > 0 ? 'owed' : refundCents > 0 || unappliedCents > 0 ? 'review' : 'settled',
    dueCents,
    refundCents,
    unappliedCents,
    overpaidCents: 0,
    sideNoteCents,
  };
}

export const BALANCE_STATE_LABEL_FR: Record<BalanceState, string> = {
  paid: 'Payé',
  partial: 'Partiellement payé',
  unpaid: 'Non payé',
  owed: 'Impayé',
  review: 'À régulariser',
  settled: 'Soldé',
  none: '—',
};

export const BALANCE_PHASE_LABEL_FR: Record<BalancePhase, string> = {
  future: 'à venir',
  current: 'en séjour',
  past: 'parti',
};

/** Green for nothing left, red for money owed, amber for the rest — the
 *  « Paiement » column's semantic colours (UI-BRANDING §3.2). */
export function stateTone(s: BalanceState): 'ok' | 'warn' | 'partial' | 'none' {
  if (s === 'paid' || s === 'settled') return 'ok';
  if (s === 'unpaid' || s === 'owed') return 'warn';
  if (s === 'partial' || s === 'review') return 'partial';
  return 'none';
}

// ── The list: filters, search, sort ─────────────────────────────────────────

/** The tiles on top double as the filter. */
export type BalanceFilter = 'owed' | 'review' | 'notes' | 'current' | 'future' | 'all';

export interface ReadRow {
  b: BookingBalance;
  r: BalanceReading;
}

export function readRows(rows: readonly BookingBalance[], today: IsoDate): ReadRow[] {
  return rows.map((b) => ({ b, r: readBalance(b, today) }));
}

export function inFilter(x: ReadRow, f: BalanceFilter): boolean {
  switch (f) {
    case 'owed':
      return x.r.phase === 'past' && x.r.state === 'owed';
    case 'review':
      return x.r.phase === 'past' && x.r.state === 'review';
    case 'notes':
      return x.r.phase === 'past' && x.r.sideNoteCents > 0;
    case 'current':
      return x.r.phase === 'current';
    case 'future':
      return x.r.phase === 'future';
    case 'all':
      return true;
  }
}

export interface FilterTotals {
  count: number;
  /** owed: what is owed · review: owed back + arrhes never applied ·
   *  notes: the side notes · current, future: what is left to pay ·
   *  all: what is owed or left. */
  cents: number;
  /** notes: how many side notes (a booking may hold several). */
  noteCount: number;
  /** future: of them, bookings with nothing paid yet. */
  nothingPaid: number;
  /** future: the arrhes and other payments already in. */
  paidCents: number;
}

export function filterTotals(list: readonly ReadRow[], f: BalanceFilter): FilterTotals {
  const mine = list.filter((x) => inFilter(x, f));
  const amount = (x: ReadRow) =>
    f === 'review' ? x.r.refundCents + x.r.unappliedCents : f === 'notes' ? x.r.sideNoteCents : x.r.dueCents;
  return {
    count: mine.length,
    cents: mine.reduce((t, x) => t + amount(x), 0),
    noteCount: f === 'notes' ? sideNotes(mine).length : 0,
    nothingPaid: mine.filter((x) => x.b.paidCents <= 0 && x.b.totalCents > 0).length,
    paidCents: mine.reduce((t, x) => t + x.b.paidCents, 0),
  };
}

/** Lower case, accents off — « Léger » finds « LEGER ». */
export function fold(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** Every word of the query must be found in the booking's name, Vega number,
 *  client number, channel reference, rooms or the guests its notes name — a
 *  number also inside Vega's six digits, so « 24696 » finds 024696 (as the
 *  planning's search does), and « gingell » finds 022177 by its side note. */
export function matchesQuery(b: BookingBalance, query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = fold(
    [
      b.partyLabel,
      b.bookingRef ?? '',
      b.clientRef ?? '',
      b.externalRef ?? '',
      ...b.rooms,
      ...b.rooms.map(displayRoom),
      ...b.openNotes.map((n) => n.guest ?? ''),
    ].join(' '),
  );
  return words.every((w) => hay.includes(w));
}

export type BalanceSort = 'date' | 'name' | 'due';

/** By date: the soonest arrival first for bookings to come, the latest
 *  departure first otherwise (the most recent unpaid on top). */
export function sortRows(list: readonly ReadRow[], sort: BalanceSort, filter: BalanceFilter): ReadRow[] {
  const byName = (x: ReadRow, y: ReadRow) => x.b.partyLabel.localeCompare(y.b.partyLabel, 'fr');
  const out = [...list];
  if (sort === 'name') return out.sort(byName);
  if (sort === 'due') {
    const amount = (x: ReadRow) =>
      x.r.state === 'review'
        ? x.r.refundCents + x.r.unappliedCents
        : x.r.dueCents > 0
          ? x.r.dueCents
          : x.r.sideNoteCents;
    return out.sort((x, y) => amount(y) - amount(x) || byName(x, y));
  }
  if (filter === 'future') {
    return out.sort((x, y) => x.b.arrivalDate.localeCompare(y.b.arrivalDate) || byName(x, y));
  }
  return out.sort((x, y) => y.b.departureDate.localeCompare(x.b.departureDate) || byName(x, y));
}

/** Days from one ISO date to another (negative when `to` is earlier). */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return to >= from ? nightsBetween(from, to) : -nightsBetween(to, from);
}

/** A booking to come within this many days with nothing paid gets a flag —
 *  information for the desk, not a deposit rule. */
export const SOON_DAYS = 7;

/** « J-3 · rien réglé »: a booking arriving soon with nothing paid yet. */
export function soonUnpaid(x: ReadRow, today: IsoDate): number | null {
  if (x.r.phase !== 'future' || x.b.paidCents > 0 || x.b.totalCents <= 0) return null;
  const d = daysBetween(today, x.b.arrivalDate);
  return d <= SOON_DAYS ? d : null;
}

// ── « Notes en attente » — one row per side note ────────────────────────────

export interface SideNoteRow {
  x: ReadRow;
  note: BookingOpenNote;
  /** Days since the note's date (else the departure). */
  ageDays: number;
}

/** The side notes of these bookings, one per row: what the desk works through
 *  — collect it, cancel it in Vega, or put it on a debtor account. */
export function sideNotes(list: readonly ReadRow[], today?: IsoDate): SideNoteRow[] {
  const out: SideNoteRow[] = [];
  for (const x of list) {
    if (x.r.sideNoteCents <= 0) continue;
    for (const note of x.b.openNotes) {
      if (note.cents <= 0) continue;
      out.push({ x, note, ageDays: today ? daysBetween(note.date ?? x.b.departureDate, today) : 0 });
    }
  }
  return out;
}

/** By date: the most recent note first, as the rest of the sheet; by name: the
 *  party, then the room's guest; by amount: the largest first. */
export function sortSideNotes(list: readonly SideNoteRow[], sort: BalanceSort): SideNoteRow[] {
  const byName = (p: SideNoteRow, q: SideNoteRow) =>
    p.x.b.partyLabel.localeCompare(q.x.b.partyLabel, 'fr') || (p.note.guest ?? '').localeCompare(q.note.guest ?? '', 'fr');
  const out = [...list];
  if (sort === 'name') return out.sort(byName);
  if (sort === 'due') return out.sort((p, q) => q.note.cents - p.note.cents || byName(p, q));
  return out.sort((p, q) => p.ageDays - q.ageDays || byName(p, q));
}
